import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import Breadcrumb from "../../../../Components/Utils/Breadcrumb";
import { CreateButton } from "../../../../Components/Utils/Buttons";
import SearchBar from "../../../../Components/Utils/SearchBar";
import ViewIcon from "../../../../Components/Icons/ViewIcon";
import Edit from "../../../../Components/Icons/Edit";
import Delete from "../../../../Components/Icons/Delete";
import UseGetDataSearch from "../../../../Components/Hooks/useFetchDataBySearch";
import Pagination from "../../../../Components/Utils/Pagenation";
import NuAmount from "../../../../Components/Utils/NuAmount";
import NuDate from "../../../../Components/Utils/NuDate";
import ModalContainer from "../../../../Components/Others/ModalContainer";
import { useDeleteData } from "../../../../Components/Hooks/useDeleteData";
import { CheckDeleteAction, CheckEditAction, GetAccess, GetRole } from "../../../../Components/Utils/roles";

const ShippingComparisonIndex = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const currentPage = location.state?.currentPage;
  const searchQuery = location.state?.searchQuery;

  const [page, setPage] = useState(currentPage || 1);
  const [pageCount, setPageCount] = useState(
    Number(localStorage.getItem("perPageView")) || 20
  );
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedDeleteId, setSelectedDeleteId] = useState(null);

  const [params, setParams] = useState({
    page: page,
    per_page: pageCount,
    search: searchQuery || "",
  });

  const canCreate = GetAccess("shipping_comparison_create") || GetAccess("shipping_comparison_view") || GetRole();
  const canEdit = GetAccess("shipping_comparison_edit") || GetAccess("shipping_comparison_view") || GetRole();
  const canDelete = GetAccess("shipping_comparison_delete") || GetAccess("shipping_comparison_view") || GetRole();
  const isAdmin = GetRole();

  const { data, totalCount, total, getData, dataLoading } = UseGetDataSearch(
    "/shipping-comparison/all",
    params
  );

  const { handle: handleDeleteData } = useDeleteData(getData);

  useEffect(() => {
    setParams((prev) => ({
      ...prev,
      page: page,
      per_page: pageCount,
    }));
  }, [page, pageCount]);

  const handleOpenDelete = (id) => {
    setSelectedDeleteId(id);
    setDeleteModalOpen(true);
  };

  const handleConfirmDelete = () => {
    if (selectedDeleteId) {
      handleDeleteData(
        `/shipping-comparison/delete/${selectedDeleteId}`,
        setDeleteModalOpen
      );
    }
  };

  const comparisonList = Array.isArray(data)
    ? data
    : Array.isArray(data?.data)
      ? data.data
      : [];

  const totalRecords = totalCount || total || (Array.isArray(data) ? data.length : 0);

  return (
    <div className="w-full h-full px-2 sm:px-4 py-2">
      {/* Top Header Bar */}
      <div className="w-full flex flex-col md:flex-row justify-between items-stretch md:items-center gap-2 md:gap-3 mb-3">
        <div className="w-full md:w-auto flex justify-between items-center gap-2">
          <Breadcrumb
            items={[
              { name: "Shipping", link: "/shipping/shipping-comparison" },
              { name: "Shipping Comparison" },
            ]}
          />
          <div className="flex md:hidden items-center gap-2">
            {canCreate && (
              <Link to="/shipping/shipping-comparison/create">
                <CreateButton label="Comparison" />
              </Link>
            )}
          </div>
        </div>

        <div className="w-full md:w-auto flex flex-wrap md:flex-nowrap items-center gap-2">
          <div className="w-full md:w-64">
            <SearchBar
              handleChange={(e) =>
                setParams((prev) => ({ ...prev, search: e.target.value, page: 1 }))
              }
              value={params?.search}
              placeholder="Search..."
            />
          </div>

          <div className="hidden md:flex items-center gap-2">
            {canCreate && (
              <Link to="/shipping/shipping-comparison/create">
                <CreateButton label="Comparison" />
              </Link>
            )}
          </div>
        </div>
      </div>

      {/* Main Table Container */}
      <div className="w-full h-[calc(100vh-130px)] rounded-md bg-white p-3 mt-4 flex flex-col justify-between">
        <div className="w-full h-[calc(100vh-200px)] overflow-x-auto overflow-y-auto">
          <table className="w-full min-w-[850px] border-collapse">
            <thead className="sticky top-0 bg-white z-10 border-b border-black">
              <tr className="text-xs font-semibold text-[#111537]">
                <th className="w-[36%] text-left p-2 font-medium truncate">Comparison Details</th>
                <th className="w-[18%] text-left p-2 font-medium truncate">Agent 1</th>
                <th className="w-[18%] text-left p-2 font-medium truncate">Agent 2</th>
                <th className="w-[18%] text-left p-2 font-medium truncate">Agent 3</th>
                <th className="w-[10%] text-center p-2 font-medium truncate">Actions</th>
              </tr>
            </thead>
            <tbody className="text-xs w-full divide-y divide-gray-100">
              {dataLoading ? (
                <tr>
                  <td colSpan={5}>
                    <div className="w-full h-[45vh] flex justify-center items-center">
                      <div className="flex flex-row gap-2">
                        <div className="w-3 h-3 rounded-full bg-baseColor-primary animate-bounce" />
                        <div className="w-3 h-3 rounded-full bg-baseColor-primary animate-bounce [animation-delay:-.3s]" />
                        <div className="w-3 h-3 rounded-full bg-baseColor-primary animate-bounce [animation-delay:-.5s]" />
                        <div className="w-3 h-3 rounded-full bg-baseColor-primary animate-bounce [animation-delay:-.7s]" />
                      </div>
                    </div>
                  </td>
                </tr>
              ) : comparisonList.length > 0 ? (
                comparisonList.map((value, index) => {
                  const agents = Array.isArray(value?.agents)
                    ? value.agents
                    : Array.isArray(value?.agents_detail)
                      ? value.agents_detail
                      : [];

                  const validPrices = agents
                    .map((a) => Number(a?.price))
                    .filter((p) => !isNaN(p) && p > 0);

                  const minPrice =
                    validPrices.length > 0 ? Math.min(...validPrices) : 0;
                  const maxPrice =
                    validPrices.length > 0 ? Math.max(...validPrices) : 0;

                  const getPriceBg = (price) => {
                    const num = Number(price);
                    if (!num || isNaN(num) || num <= 0 || minPrice <= 0) return "";
                    if (num === minPrice) return "bg-green-100";
                    if (num === maxPrice && minPrice !== maxPrice) return "bg-red-100";
                    return "";
                  };

                  return (
                    <tr
                      key={value?.id || index}
                      className="odd:bg-white even:bg-slate-50 border-b border-gray-100 hover:bg-slate-100 text-[#606279]"
                    >
                      {/* Comparison Details / PR */}
                      <td className="p-2 text-left">
                        <div className="flex flex-col gap-0.5">
                          <div className="flex items-center gap-1.5 flex-wrap">
                            <span className="font-semibold text-gray-900 text-xs">
                              {value?.description || `Comparison #${value?.id}`}
                            </span>
                            {value?.prRef && (
                              <span className="text-[10px] bg-teal-50 text-[#048178] px-1.5 py-0.5 rounded font-medium border border-teal-200">
                                {value.prRef}
                              </span>
                            )}
                            {value?.poRef && (
                              <span className="text-[10px] bg-blue-50 text-blue-700 px-1.5 py-0.5 rounded font-medium border border-blue-200">
                                {value.poRef}
                              </span>
                            )}
                          </div>

                          <div className="text-[11px] text-gray-500 flex items-center gap-2 mt-0.5">
                            <span>
                              {value?.port ? `${value.port} → ${value?.destination || "?"}` : value?.shipmenttype || "N/A"}
                            </span>
                            {value?.createdAt && (
                              <span className="text-gray-400">
                                • {NuDate({ value: value.createdAt, format: "DD-MMM-YYYY" })}
                              </span>
                            )}
                          </div>
                        </div>
                      </td>

                      {/* Agent 1 */}
                      <td className={`p-2 text-left ${getPriceBg(agents[0]?.price)}`}>
                        {agents[0] ? (
                          <div className="flex flex-col gap-0.5">
                            <div className="flex items-center gap-1">
                              <span
                                className={`text-xs font-semibold ${agents[0]?.isSelected
                                  ? "text-emerald-700 font-bold"
                                  : "text-gray-800"
                                  }`}
                              >
                                {agents[0]?.agentName || agents[0]?.name || "Agent 1"}
                              </span>
                              {agents[0]?.isSelected && (
                                <span className="text-[9px] bg-emerald-100 text-emerald-800 px-1 py-0.2 rounded font-semibold">
                                  ✓ Selected
                                </span>
                              )}
                            </div>
                            <div className="flex items-center gap-1.5">
                              <span
                                className={`text-xs font-semibold ${
                                  minPrice > 0 && Number(agents[0]?.price) === minPrice
                                    ? "text-emerald-800 font-bold"
                                    : maxPrice > 0 && Number(agents[0]?.price) === maxPrice && minPrice !== maxPrice
                                      ? "text-red-800 font-bold"
                                      : "text-gray-700"
                                }`}
                              >
                                <NuAmount
                                  currency={value?.currency || agents[0]?.currency || "USD"}
                                  value={agents[0]?.price || 0}
                                />
                              </span>
                              {isAdmin && (
                                <Link
                                  to={`/shipping/shipping-comparison/priceView/${value?.id}/${agents[0]?.id}`}
                                  className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 bg-[#048178] hover:bg-[#036962] text-white text-[10px] font-bold rounded-full transition-colors ml-1"
                                  title="View Revision History"
                                >
                                  {Array.isArray(agents[0]?.history)
                                    ? agents[0].history.length
                                    : agents[0]?.revisionCount || 0}
                                </Link>
                              )}
                            </div>
                          </div>
                        ) : (
                          <span className="text-gray-300 italic text-[11px]">-</span>
                        )}
                      </td>

                      {/* Agent 2 */}
                      <td className={`p-2 text-left ${getPriceBg(agents[1]?.price)}`}>
                        {agents[1] ? (
                          <div className="flex flex-col gap-0.5">
                            <div className="flex items-center gap-1">
                              <span
                                className={`text-xs font-semibold ${agents[1]?.isSelected
                                  ? "text-emerald-700 font-bold"
                                  : "text-gray-800"
                                  }`}
                              >
                                {agents[1]?.agentName || agents[1]?.name || "Agent 2"}
                              </span>
                              {agents[1]?.isSelected && (
                                <span className="text-[9px] bg-emerald-100 text-emerald-800 px-1 py-0.2 rounded font-semibold">
                                  ✓ Selected
                                </span>
                              )}
                            </div>
                            <div className="flex items-center gap-1.5">
                              <span
                                className={`text-xs font-semibold ${
                                  minPrice > 0 && Number(agents[1]?.price) === minPrice
                                    ? "text-emerald-800 font-bold"
                                    : maxPrice > 0 && Number(agents[1]?.price) === maxPrice && minPrice !== maxPrice
                                      ? "text-red-800 font-bold"
                                      : "text-gray-700"
                                }`}
                              >
                                <NuAmount
                                  currency={value?.currency || agents[1]?.currency || "USD"}
                                  value={agents[1]?.price || 0}
                                />
                              </span>
                              {isAdmin && (
                                <Link
                                  to={`/shipping/shipping-comparison/priceView/${value?.id}/${agents[1]?.id}`}
                                  className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 bg-[#048178] hover:bg-[#036962] text-white text-[10px] font-bold rounded-full transition-colors ml-1"
                                  title="View Revision History"
                                >
                                  {Array.isArray(agents[1]?.history)
                                    ? agents[1].history.length
                                    : agents[1]?.revisionCount || 0}
                                </Link>
                              )}
                            </div>
                          </div>
                        ) : (
                          <span className="text-gray-300 italic text-[11px]">-</span>
                        )}
                      </td>

                      {/* Agent 3 */}
                      <td className={`p-2 text-left ${getPriceBg(agents[2]?.price)}`}>
                        {agents[2] ? (
                          <div className="flex flex-col gap-0.5">
                            <div className="flex items-center gap-1">
                              <span
                                className={`text-xs font-semibold ${agents[2]?.isSelected
                                  ? "text-emerald-700 font-bold"
                                  : "text-gray-800"
                                  }`}
                              >
                                {agents[2]?.agentName || agents[2]?.name || "Agent 3"}
                              </span>
                              {agents[2]?.isSelected && (
                                <span className="text-[9px] bg-emerald-100 text-gree-800 px-1 py-0.2 rounded font-semibold">
                                  ✓ Selected
                                </span>
                              )}
                            </div>
                            <div className="flex items-center gap-1.5">
                              <span
                                className={`text-xs font-semibold ${
                                  minPrice > 0 && Number(agents[2]?.price) === minPrice
                                    ? "text-emerald-800 font-bold"
                                    : maxPrice > 0 && Number(agents[2]?.price) === maxPrice && minPrice !== maxPrice
                                      ? "text-red-800 font-bold"
                                      : "text-gray-700"
                                }`}
                              >
                                <NuAmount
                                  currency={value?.currency || agents[2]?.currency || "USD"}
                                  value={agents[2]?.price || 0}
                                />
                              </span>
                              {isAdmin && (
                                <Link
                                  to={`/shipping/shipping-comparison/priceView/${value?.id}/${agents[2]?.id}`}
                                  className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 bg-[#048178] hover:bg-[#036962] text-white text-[10px] font-bold rounded-full transition-colors ml-1"
                                  title="View Revision History"
                                >
                                  {Array.isArray(agents[2]?.history)
                                    ? agents[2].history.length
                                    : agents[2]?.revisionCount || 0}
                                </Link>
                              )}
                            </div>
                          </div>
                        ) : (
                          <span className="text-gray-300 italic text-[11px]">-</span>
                        )}
                      </td>

                      {/* Actions */}
                      <td className="p-2 text-center">
                        <div className="flex items-center justify-center gap-2">
                          <Link
                            to={`/shipping/shipping-comparison/view/${value?.id}`}
                            state={{
                              data: value,
                              pageIndex: page,
                              searchQuery: params?.search,
                            }}
                            className="text-gray-600 hover:text-teal-600 transition-colors"
                            title="View Details"
                          >
                            <ViewIcon />
                          </Link>

                          {canEdit && (
                            <Link
                              to={`/shipping/shipping-comparison/edit/${value?.id}`}
                              state={{
                                data: value,
                                pageIndex: page,
                                searchQuery: params?.search,
                              }}
                              className="text-gray-600 hover:text-blue-600 transition-colors"
                              title="Edit Comparison"
                            >
                              <Edit />
                            </Link>
                          )}

                          {canDelete && (
                            <button
                              type="button"
                              onClick={() => handleOpenDelete(value?.id)}
                              className="text-gray-600 hover:text-red-600 transition-colors cursor-pointer"
                              title="Delete Comparison"
                            >
                              <Delete />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan={5} className="py-12 text-center text-gray-400 text-sm">
                    No shipping comparisons found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Bottom Pagination */}
        <div className="w-full h-[40px]">
          <Pagination
            total={totalRecords}
            pageIndex={page}
            setPageIndex={setPage}
            dataCount={pageCount}
            setDataCount={setPageCount}
          />
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {deleteModalOpen && (
        <ModalContainer
          visiable={deleteModalOpen}
          title="Warning"
          content="Are you sure you want to remove this Shipping Comparison record?"
          handleClick={handleConfirmDelete}
          closeModal={() => setDeleteModalOpen(false)}
          okText="Delete"
        />
      )}
    </div>
  );
};

export default ShippingComparisonIndex;
